import React, { ReactNode, useState, useEffect } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box } from '@mui/system';
import { accordionStyles } from './styles';
import AccordionComponentsTitle from './AccordionComponentsTitle';
import usePractitionerControlsContext from '../../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import { Theme } from '../../PhaserApp/enums/Themes';

interface AccordionProps {
  className?: string;
  title: string;
  subtitle?: string;
  Icon?: React.ReactNode;
  children?: React.ReactNode;
  isClientPreview?: boolean;
  toggleValue?: boolean;
  setToggle?: Function;
  isIcon?: boolean;
  isExpanded?: boolean;
  pwid?: string;
  // Overrides the default `theme === child` check for the client-preview's child
  // background. Lets the footer hide the landscape when the child game has been
  // promoted to the main area (otherwise the landscape would show in both places).
  childThemeActive?: boolean;
  lightThemeActive?: boolean;
  // Rendered at the right end of the title bar, just before the arrow. Its clicks stay
  // out of the title bar's collapse handling so the control only acts on itself.
  headerAction?: React.ReactNode;
  // When set, the panel body becomes a click target for this handler. The title bar and
  // the arrow keep collapsing the panel, so both interactions stay unambiguous.
  onSurfaceClick?: () => void;
}

const AccordionComponent: React.FC<AccordionProps> = (props) => {
  const {
    title,
    subtitle,
    Icon,
    children,
    className,
    isClientPreview = false,
    toggleValue,
    setToggle,
    isIcon = true,
    isExpanded = false,
    pwid,
    childThemeActive,
    lightThemeActive,
    headerAction,
    onSurfaceClick,
  } = props;
  const { container } = accordionStyles;
  // Load initial state from localStorage (if available)
  const [expanded, setExpanded] = useState(() => {
    const storedState = localStorage.getItem(`accordion_${title}`);
    return storedState ? JSON.parse(storedState) : isExpanded;
  });
  const { theme } = usePractitionerControlsContext();
  // Caller may override; otherwise fall back to the theme value.
  const showChildTheme = childThemeActive ?? theme === Theme.Child;
  const showLightTheme = lightThemeActive ?? theme === Theme.Light;
  // Update localStorage whenever the accordion state changes
  useEffect(() => {
    localStorage.setItem(`accordion_${title}`, JSON.stringify(expanded));
  }, [expanded, title]);

  const toggleHandler = () => {
    if (setToggle) {
      setToggle();
    }
    setExpanded(!expanded);
  };

  const isSurfaceClickable = Boolean(onSurfaceClick);

  // The title bar keeps its usual job of collapsing the panel, so the header control
  // has to keep its click to itself. It also opens a collapsed panel: the control acts
  // on what the panel shows, and moving content into a closed panel would hide it.
  const headerActionClickHandler = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!expanded) {
      toggleHandler();
    }
  };

  return (
    <Box key={title} sx={container} className={className}>
      <Accordion
        onChange={toggleHandler}
        expanded={expanded}
        className={`accordion ${isClientPreview ? 'client-preview-accordion' : ''}`}
      >
        <AccordionSummary
          className={`accordion-summary ${
            !isClientPreview ? 'accordion-summary-background' : 'accordion-summary-client-background'
          }`}
          expandIcon={
            <KeyboardArrowDownIcon className={`arrow ${isClientPreview ? 'arrow-icon' : ''}`} fontSize="large" />
          }
          aria-controls={pwid ? `${pwid}-content` : 'panel1-content'}
          id={pwid ?? 'panel1-header'}
          data-testid={pwid}
        >
          {isIcon && <Box className="summary-icon-container">{Icon}</Box>}
          {/* Assuming you have a component for rendering the title and subtitle */}
          <AccordionComponentsTitle title={title} subtitle={subtitle} />
          {headerAction ? (
            <Box className="summary-action" onClick={headerActionClickHandler}>
              {headerAction}
            </Box>
          ) : null}
        </AccordionSummary>
        <AccordionDetails
          className={
            (title === 'notes'
              ? 'notes'
              : !isClientPreview
                ? 'accordion-details'
                : 'accordion-details-client-view' +
                  (showChildTheme ? ' child-theme' : showLightTheme ? ' light-theme' : '')) +
            (isSurfaceClickable ? ' surface-clickable' : '')
          }
          onClick={onSurfaceClick}
          id={pwid ? `${pwid}-content` : undefined}
          data-testid={pwid ? `${pwid}-content` : undefined}
        >
          {children}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AccordionComponent;
